import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadIcon, UploadIcon, WifiIcon, ShieldAlertIcon, HomeIcon, ArrowRightIcon, ZapIcon, ServerIcon, UsersIcon, SearchIcon, CheckCircleIcon, SettingsIcon, HeadphonesIcon, HotelIcon, ShoppingBagIcon, BriefcaseIcon, ClockIcon, ActivityIcon, HelpCircleIcon, ChevronDownIcon, StarIcon, TrendingUpIcon } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
export function BusinessInternetPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div data-id="element-718">
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-719">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-720"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-721"></div>
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[100px] pointer-events-none" data-id="element-722"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-723">
          <div className="max-w-3xl" data-id="element-724">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-725">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-726">
                <span className="flex h-2 w-2 rounded-full bg-blue-400 mr-2" data-id="element-727"></span>
                Enterprise-Grade Connectivity
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-728">
                High-Performance
                <br className="hidden md:block" data-id="element-729" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-730">
                  Business Internet
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-731">
                In today's fast-paced business environment, a reliable and
                high-speed internet connection is not just a luxury—it's a
                necessity. Whether you're running a small startup or a large
                enterprise, your business's productivity and success depend on
                the quality of your internet service. At Ambient Telecom, we
                offer customized business internet solutions tailored to meet
                your specific needs.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-732">
                Check Availability
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-733" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Download vs Upload */}
      <section className="py-20 bg-white" data-id="element-734">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-735">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-736">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-737">
              Download Speed Vs Upload Speed
            </h2>
            <p className="text-lg text-slate-600" data-id="element-738">
              When choosing a business internet plan, it's important to
              understand the difference between download and upload speeds to
              ensure your team has the bandwidth they need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-739">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 relative overflow-hidden" data-id="element-740">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-bl-full opacity-50 -z-10" data-id="element-741"></div>
              <div className="w-14 h-14 bg-white rounded-xl shadow-sm border border-slate-100 flex items-center justify-center mb-6" data-id="element-742">
                <DownloadIcon className="w-7 h-7 text-blue-500" data-id="element-743" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-744">
                Download Speed
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-745">
                This measures how fast data can be downloaded from the internet
                to your devices. It's crucial for activities like streaming
                videos, downloading large files, and browsing the web
                efficiently without lag.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 relative overflow-hidden" data-id="element-746">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-100 rounded-bl-full opacity-50 -z-10" data-id="element-747"></div>
              <div className="w-14 h-14 bg-white rounded-xl shadow-sm border border-slate-100 flex items-center justify-center mb-6" data-id="element-748">
                <UploadIcon className="w-7 h-7 text-green-500" data-id="element-749" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-750">
                Upload Speed
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-751">
                This measures how fast data can be sent from your devices to the
                internet. It's essential for video conferencing, uploading large
                files to the cloud, and running online applications smoothly.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Speed Tiers */}
      <section className="py-20 bg-slate-50" data-id="element-752">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-753">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-754">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-755">
              Find the Perfect Business Internet Speed
            </h2>
            <p className="text-lg text-slate-600" data-id="element-756">
              Ambient Telecom provides a reliable, scalable internet solution
              with up to 2 Gbps download and 880 Mbps upload speeds, enhancing
              connections through the largest gig-speed networks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-757">
            {[{
            speed: '150 Mbps – 300 Mbps',
            users: '1 to 3 users',
            desc: 'Handling basic tasks like downloading files and sending emails.',
            icon: UsersIcon,
            color: 'text-blue-500',
            bg: 'bg-blue-50'
          }, {
            speed: '500 Mbps – 800 Mbps',
            users: '3 to 5 users',
            desc: 'Perfect for small businesses needing to share files, stream videos, and process transactions efficiently.',
            icon: ZapIcon,
            color: 'text-primary',
            bg: 'bg-primary/10'
          }, {
            speed: '1.25 Gbps – 2 Gbps',
            users: '5 to 7+ users',
            desc: 'Suited for growing businesses, capable of supporting video conferencing and large data backups.',
            icon: ServerIcon,
            color: 'text-purple-500',
            bg: 'bg-purple-50'
          }].map((tier, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-758">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${tier.bg}`} data-id="element-759">
                  <tier.icon className={`w-6 h-6 ${tier.color}`} data-id="element-760" />
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900 mb-2" data-id="element-761">
                  {tier.speed}
                </h3>
                <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-762">
                  Great for {tier.users}
                </div>
                <p className="text-slate-600 leading-relaxed" data-id="element-763">{tier.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SLA & Uptime Guarantee */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-764">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-765">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-766">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-767">
              Enterprise-Grade SLA Guarantees
            </h2>
            <p className="text-lg text-slate-400" data-id="element-768">
              We stand behind our network with industry-leading service level
              agreements.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-769">
            {[{
            icon: ActivityIcon,
            title: '99.9% Uptime SLA',
            desc: 'Guaranteed network availability for your critical operations.'
          }, {
            icon: ClockIcon,
            title: '4-Hour Response',
            desc: 'Rapid response times for any critical network incidents.'
          }, {
            icon: UsersIcon,
            title: 'Dedicated Account Manager',
            desc: 'A single point of contact who knows your business.'
          }, {
            icon: ShieldAlertIcon,
            title: 'Proactive Monitoring',
            desc: 'We often fix issues before you even notice them.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-id="element-770">
                <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-771">
                  <item.icon className="w-7 h-7 text-primary" data-id="element-772" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-773">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-774">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-id="element-775">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-776">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-777">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-778">
              Our Implementation Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-779">
              We handle the complexity of switching or upgrading your internet
              so you can focus on your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative" data-id="element-780">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-100 -translate-y-12 z-0" data-id="element-781"></div>

            {[{
            step: '01',
            title: 'Bill Audit',
            desc: 'We analyze your current spend and performance to identify overcharges and bottlenecks.',
            icon: SearchIcon
          }, {
            step: '02',
            title: 'Provider Selection',
            desc: 'We leverage our 240+ supplier network to find the best speed and price for your location.',
            icon: CheckCircleIcon
          }, {
            step: '03',
            title: 'Seamless Install',
            desc: 'Our project managers coordinate the installation to ensure zero downtime during the transition.',
            icon: SettingsIcon
          }, {
            step: '04',
            title: 'Ongoing Support',
            desc: 'We provide 24/7 monitoring and support to ensure your connection stays rock solid.',
            icon: HeadphonesIcon
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative z-10 text-center" data-id="element-782">
                <div className="w-16 h-16 bg-primary text-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary/20" data-id="element-783">
                  <item.icon className="w-8 h-8" data-id="element-784" />
                </div>
                <div className="text-primary font-bold text-sm mb-2" data-id="element-785">
                  STEP {item.step}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-786">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-787">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-24 bg-slate-900 relative overflow-hidden" data-id="element-788">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-789"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-790">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-791">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-792">
              Tailored Industry Solutions
            </h2>
            <p className="text-lg text-slate-400" data-id="element-793">
              Different businesses have different needs. We specialize in
              high-performance internet for specific sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-794">
            {[{
            title: 'Hospitality',
            desc: 'Reliable guest WiFi and high-bandwidth backbones for hotels and resorts.',
            icon: HotelIcon
          }, {
            title: 'Retail',
            desc: 'Secure, fast connections for POS systems and inventory management across multiple sites.',
            icon: ShoppingBagIcon
          }, {
            title: 'Professional Services',
            desc: 'Dedicated bandwidth for law firms, accounting, and consulting agencies requiring 100% uptime.',
            icon: BriefcaseIcon
          }].map((industry, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors" data-id="element-795">
                <industry.icon className="w-12 h-12 text-primary mb-6" data-id="element-796" />
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-797">
                  {industry.title}
                </h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-798">
                  {industry.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Real Results */}
      <section className="py-24 bg-slate-50" data-id="element-799">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-800">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-801">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-802">
              Real Results for Real Businesses
            </h2>
            <p className="text-lg text-slate-600" data-id="element-803">
              See how we've helped organizations transform their connectivity.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-804">
            {[{
            industry: 'Law Firm',
            result: 'Saved 40%',
            desc: 'Switched from legacy T1 lines to dedicated fiber, reducing costs while increasing bandwidth by 10x.',
            icon: TrendingUpIcon
          }, {
            industry: 'Restaurant Chain',
            result: '3x Faster Speeds',
            desc: 'Upgraded 15 locations to business-class cable internet at the exact same monthly cost.',
            icon: ZapIcon
          }, {
            industry: 'Medical Office',
            result: 'HIPAA Compliant',
            desc: 'Implemented secure, encrypted connections with automatic 4G LTE failover for patient data.',
            icon: ShieldAlertIcon
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm" data-id="element-805">
                <div className="flex items-center gap-2 mb-4 text-amber-400" data-id="element-806">
                  {[...Array(5)].map((_, j) => <StarIcon key={j} className="w-5 h-5 fill-current" data-id="element-807" />)}
                </div>
                <div className="text-sm font-bold text-primary uppercase tracking-wider mb-2" data-id="element-808">
                  {item.industry}
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-4" data-id="element-809">
                  {item.result}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-810">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Wifi & Backup */}
      <section className="py-20 bg-white" data-id="element-811">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-812">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-813">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-814">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-815">
                Advanced WiFi & Automatic Backup
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-816">
                We don't just provide the connection; we ensure it's secure,
                accessible, and always online when you need it most.
              </p>

              <div className="space-y-8" data-id="element-817">
                <div className="flex" data-id="element-818">
                  <div className="flex-shrink-0 mt-1" data-id="element-819">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-820">
                      <WifiIcon className="w-6 h-6 text-blue-600" data-id="element-821" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-822">
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-823">
                      Advanced WiFi Solutions
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-824">
                      With Business WiFi Pro, you get two networks—one private
                      for your team and one for guests. Advanced features like
                      customizable splash pages help drive business and track
                      trends.
                    </p>
                  </div>
                </div>

                <div className="flex" data-id="element-825">
                  <div className="flex-shrink-0 mt-1" data-id="element-826">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center" data-id="element-827">
                      <ShieldAlertIcon className="w-6 h-6 text-red-600" data-id="element-828" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-829">
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-830">
                      Automatic Backup
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-831">
                      Business Connection Pro ensures your business stays
                      connected during internet outages. With 4G LTE wireless
                      backup, you can maintain connectivity for up to 8 to 16
                      hours, even during a power outage.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-832">
              <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=600&fit=crop" alt="Server room networking equipment" className="w-full h-auto object-cover" data-id="element-833" />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 to-transparent" data-id="element-834"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Work from Home */}
      <section className="py-20 bg-primary relative overflow-hidden" data-id="element-835">
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)'
      }} data-id="element-836"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-837">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm" data-id="element-838">
            <HomeIcon className="w-8 h-8 text-white" data-id="element-839" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-840">
            Can Business Internet Be Used at Home?
          </h2>
          <p className="text-xl text-blue-100 leading-relaxed mb-8" data-id="element-841">
            Absolutely! Business internet plans are not just for office
            environments—they can also be used at home. Whether you're running a
            home office, managing a remote team, or simply need a more powerful
            connection for personal use, our business internet solutions provide
            the speed and reliability you need.
          </p>
          <p className="text-lg text-white font-medium" data-id="element-842">
            With higher performance than standard residential internet, our
            business plans are perfect for ensuring your work-from-home setup is
            just as efficient as being in the office.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection data-id="element-843" />

      {/* Frequently Asked Questions */}
      <section className="py-24 bg-slate-50" data-id="element-844">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-845">
          <div className="text-center mb-16" data-id="element-846">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-847">
              <HelpCircleIcon className="w-8 h-8 text-primary" data-id="element-848" />
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-849">
              Common questions about our business internet services.
            </p>
          </div>
          <div className="space-y-4" data-id="element-850">
            {[{
            q: "What's the difference between business and residential internet?",
            a: 'Business internet offers higher reliability, faster upload speeds, dedicated support, service level agreements (SLAs), and features like static IPs that residential connections lack.'
          }, {
            q: 'Can I bundle services?',
            a: 'Yes! We highly recommend bundling your internet with our UCaaS phone systems or network security services for maximum savings and simplified billing.'
          }, {
            q: 'What about contracts?',
            a: 'We offer flexible terms ranging from month-to-month to 36-month agreements. Longer terms typically secure lower monthly rates and waived installation fees.'
          }, {
            q: 'How fast is installation?',
            a: 'Standard coax or fiber installations typically take 7-14 business days. Dedicated enterprise fiber builds may take 30-90 days depending on construction requirements.'
          }, {
            q: 'Do you offer static IPs?',
            a: 'Yes, static IP addresses are available with all our business internet plans, essential for hosting servers, VPNs, or advanced security systems.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-851">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-852">
                  <span className="font-bold text-slate-900" data-id="element-853">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-854">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-855" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-856">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-857">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-858">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white border-t border-slate-100" data-id="element-859">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-860">
          <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-861">
            Ready to upgrade your connection?
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-id="element-862">
            Let our experts find the perfect internet solution for your specific
            business needs and location.
          </p>
          <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg" data-id="element-863">
            Get a Custom Quote
          </button>
        </div>
      </section>
    </div>;
}